<script lang="ts">
import { defineComponent, ref, onMounted, computed } from 'vue'
import { useStore } from '@/store'
import { useI18n } from 'vue-i18n'
import { leaseRateActions } from '@/store/leaseRates/actions'
import { LeaseRateItem } from '@/store/leaseRates/index'
import Loader from '@/components/UI/Loader.vue'
import Button from '@/components/UI/Button.vue'
import InputElement from '@/components/UI/Input.vue'
import ListingTable from '@/components/UI/ListingTable.vue'
import Popup from '@/components/Popup.vue'
import SelectionPopup from '@/components/UI/Popup.vue'
import Dropdown from '@/components/UI/Dropdown.vue'
import { useCompanyId } from '@/composables/useCompanyId'
import { CompanyActionTypes } from '@/store/company/actions'
import { companyLeaseRateMappingType, companyResponseType, LeaseRateTableHeading, ApiErrorResponse } from '@/types'

// ===== Constants & Types =====
const MAPPING_FIELDS = ['leaseRate', 'fuelAdvance', 'vgsEpCode', 'mileage', 'duration'] as const

// Supported manual mileage/duration values (must match the backend's allowed combinations)
const MILEAGE_OPTIONS: number[] = [20000, 25000, 30000, 35000, 40000, 45000]
const DURATION_OPTIONS: number[] = [36, 48, 60]

type MappingField = typeof MAPPING_FIELDS[number]

type LeaseRateErrorItem = {
  error: string;
  vehicleId?: string | number;
  [key: string]: unknown;
}

const EMPTY_MAPPING = (): companyLeaseRateMappingType => ({
  leaseRate: '',
  fuelAdvance: '',
  vgsEpCode: '',
  mileage: '',
  duration: ''
})

interface ParsedError {
  key: string;
  params: Record<string, string>;
}

interface ErrorPattern {
  regex: RegExp;
  key: string;
  extract: (match: RegExpMatchArray) => Record<string, string>;
}

// NOTE: These regex patterns parse backend error messages.
const ERROR_PATTERNS: ErrorPattern[] = [
  {
    regex: /Missing required columns: (.+)/,
    key: 'missingRequiredColumns',
    extract: (m) => ({ columns: m[1] })
  },
  {
    regex: /At Row Number (\d+) Unsupported mileage\/duration combination/,
    key: 'unsupportedMileageDurationCombination',
    extract: (m) => ({ rowId: m[1] })
  },
  {
    regex: /Row (\d+) has different values \(expected: (\d+)km\/(\d+)mo\)/,
    key: 'differentMileageDurationValues',
    extract: (m) => ({ rowId: m[1], expectedMileage: m[2], expectedDuration: m[3] })
  },
  {
    regex: /At Row Number (\d+) In (.+?) value is empty/,
    key: 'valueIsEmpty',
    extract: (m) => ({ rowId: m[1], key: m[2] })
  },
  {
    regex: /At Row Number (\d+) In (.+?) scientific notation is not allowed/,
    key: 'scientificNotationNotAllowed',
    extract: (m) => ({ rowId: m[1], key: m[2] })
  },
  {
    regex: /At Row Number (\d+) In (.+?) only positive integer values are allowed/,
    key: 'onlyPositiveIntegerAllowed',
    extract: (m) => ({ rowId: m[1], key: m[2] })
  },
  {
    regex: /At Row Number (\d+) In (.+?) only positive numeric values are allowed/,
    key: 'onlyPositiveNumericAllowed',
    extract: (m) => ({ rowId: m[1], key: m[2] })
  },
  {
    regex: /At Row Number (\d+) Invalid Vehicle Id found - must be at least (\d+) characters/,
    key: 'invalidVehicleIdMinLength',
    extract: (m) => ({ rowId: m[1], minLength: m[2] })
  },
  {
    regex: /At Row Number (\d+) Invalid Vehicle Id found - must be at most (\d+) characters/,
    key: 'invalidVehicleIdMaxLength',
    extract: (m) => ({ rowId: m[1], maxLength: m[2] })
  },
  {
    regex: /At Row Number (\d+) Invalid Vehicle Id found - must contain only digits/,
    key: 'invalidVehicleIdOnlyDigits',
    extract: (m) => ({ rowId: m[1] })
  },
  {
    regex: /At Row Number (\d+) Incorrect Lease Rate Value found/,
    key: 'incorrectLeaseRateValue',
    extract: (m) => ({ rowId: m[1] })
  },
  {
    regex: /At Row Number (\d+) In (.+?) non numeric value found/,
    key: 'nonNumericValueFound',
    extract: (m) => ({ rowId: m[1], key: m[2] })
  }
]

export default defineComponent({
  name: 'LeaseRates',
  components: { Loader, Button, InputElement, ListingTable, Popup, SelectionPopup, Dropdown },

  setup() {
    const store = useStore()
    const { t } = useI18n()
    const { compId } = useCompanyId()

    // State
    const isLoading = ref(false)
    const totalRecords = ref(0)
    const uploadSuccessKey = ref('')
    const mappingErrorKey = ref('')
    const mappingSuccessKey = ref('')
    const showDeletePopup = ref(false)
    const showUploadPopup = ref(false)
    const fileInput = ref<HTMLInputElement | null>(null)
    const selectedFile = ref<File | null>(null)
    // Optional per-upload manual mileage/duration. Kept local and never persisted to the mapping.
    const manualMileage = ref('')
    const manualDuration = ref('')
    // Validation message shown inside the upload popup (so it stays visible while open).
    const uploadPopupError = ref('')
    const showErrorPopup = ref(false)
    const showSuccessPopup = ref(false)
    const isPartialSuccess = ref(false)
    const carPolicyCombinations = ref<Array<{ mileage: number; duration: number; uploaded: boolean }>>([])
    const leaseRateToDelete = ref<LeaseRateItem | null>(null)
    const errorMessages = ref<ParsedError[]>([])

    const leaseRateMapping = ref(EMPTY_MAPPING())
    const originalMappings = ref(EMPTY_MAPPING())

    const mappingErrors = ref<Record<MappingField, string>>({
      leaseRate: '',
      fuelAdvance: '',
      vgsEpCode: '',
      mileage: '',
      duration: ''
    })

    const tableHeadings = ref<LeaseRateTableHeading[]>([
      { label: 'Filename', key: 'filename', sortable: false },
      { label: 'Contract duration', key: 'contractDuration', sortable: false },
      { label: 'Yearly mileage', key: 'yearlyMileage', sortable: false },
      { label: 'Uploaded by', key: 'createdBy', sortable: false },
      { label: 'Last update', key: 'lastUpdate', sortable: false }
    ])
    const tableData = ref<LeaseRateItem[]>([])

    // Computed
    const mappingsChanged = computed(() =>
      MAPPING_FIELDS.some((f) => leaseRateMapping.value[f] !== originalMappings.value[f])
    )

    const hasValidationErrors = computed(() => Object.values(mappingErrors.value).some((error) => error !== ''))

    // Helpers
    const clearMessages = (type: 'upload' | 'mapping' | 'all' = 'all') => {
      if (type === 'upload' || type === 'all') {
        uploadSuccessKey.value = ''
      }
      if (type === 'mapping' || type === 'all') {
        mappingErrorKey.value = ''
        mappingSuccessKey.value = ''
      }
    }

    // Transform lease rate data to extract createdBy name from object
    const transformLeaseRateData = (list: LeaseRateItem[]) => {
      return list.map(item => ({
        ...item,
        createdBy: typeof item.createdBy === 'object' && item.createdBy !== null
          ? (item.createdBy as { name?: string }).name || ''
          : item.createdBy || ''
      }))
    }

    const refreshTableData = () => {
      const { leaseRateList, totalRecords: total } = store.state.leaseRates
      tableData.value = transformLeaseRateData(leaseRateList)
      totalRecords.value = total
    }

    const parseError = (text: string): ParsedError => {
      for (const pattern of ERROR_PATTERNS) {
        const match = text.match(pattern.regex)
        if (match) {
          return { key: pattern.key, params: pattern.extract(match) }
        }
      }
      return { key: 'rawError', params: { message: text } }
    }

    const extractErrorMessage = (error: ApiErrorResponse): string | string[] => {
      // Check all possible error locations and provide a fallback
      if (error?.response?.data?.leaseRateResponse) return error.response.data.leaseRateResponse
      if (error?.response?.data?.message) return error.response.data.message
      if (error?.response?.data?.error) return error.response.data.error
      if (error?.message) return error.message

      // Default fallback if no error information is available
      return 'failedToProcessFile'
    }

    const showErrorPopupWith = (
      messages: string | Array<string | { error?: unknown }> | null | undefined
    ) => {
      const normalize = (msg: string | { error?: unknown }): string =>
        typeof msg === 'string'
          ? msg
          : String(msg?.error ?? 'Unknown error occurred')

      const list = !messages
        ? ['Unknown error occurred']
        : Array.isArray(messages)
          ? messages.map(normalize)
          : [messages]

      errorMessages.value = list.map(parseError)
      showErrorPopup.value = true
    }


    const formatErrorForDisplay = (error: ParsedError): string => {
      if (error.key === 'rawError') {
        // Try to translate the raw error message if it exists in translations
        const rawMessageKey = `_leaseRates.${error.params.message}`
        const translated = t(rawMessageKey)
        return translated !== rawMessageKey ? translated : error.params.message
      }

      const translationKey = `_leaseRates.${error.key}`
      const translated = t(translationKey, error.params)

      return translated !== translationKey ? translated : error.params.message || 'Unknown error'
    }

    // Validation
    const validateMappingField = (value: string): string => {
      const v = value.trim()
      if (!v) return ''

      // Allowed characters must match the backend sanitizeString set
      // (letters, numbers, space, and - _ . , ' " ( ) : ; [ ] { } ! ? @ & / + # %)
      // so nothing gets stripped on save.
      if (/[^A-Za-z0-9 \-_.,'"():;[\]{}!?@&/+#%]/.test(v)) {
        return t('_leaseRates.mappingFieldValidationError')
      }

      return ''
    }

    const validateFile = (file?: File): string | null => {
      if (!file) return 'pleaseSelectFileToUpload'
      if (!file.size) return 'uploadedFileIsEmpty'
      if (file.size > 10 * 1024 * 1024) return 'fileSizeExceedsMaximum'

      const nameParts = file.name.toLowerCase().split('.')
      const ext = nameParts.length > 1 ? `.${nameParts[nameParts.length - 1]}` : ''
      const validExts = ['.xlsx', '.xls']
      const validMimes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ]

      if (!validExts.includes(ext) && !validMimes.includes(file.type)) {
        return 'onlyXlsxFilesAccepted'
      }
      return null
    }

    const withLoading = async (fn: () => Promise<void>) => {
      try {
        isLoading.value = true
        await fn()
      } finally {
        isLoading.value = false
      }
    }

    const normalizeResponse = (res: Record<string, unknown>) => res?.data ?? res

    // Fetch car policy combinations from company data
    const fetchCombinations = async () => {
      await store.dispatch(CompanyActionTypes.GET_COMPANYBYID, { companyId: Number(compId.value) })
      const company = store.state.company.getCompanyById as companyResponseType
      carPolicyCombinations.value = company?.carPolicyCombinations || []
    }

    // Initialization
    const initialize = () =>
      withLoading(async () => {
        await Promise.all([
          store.dispatch(leaseRateActions.GET_LEASERATE_MAPPINGS, Number(compId.value)),
          store.dispatch(leaseRateActions.GET_ALL_LEASERATES, Number(compId.value)),
          fetchCombinations()
        ])

        const { leaseRateList, totalRecords: totalCount, leaseRateMappings } = store.state.leaseRates

        tableData.value = transformLeaseRateData(leaseRateList)
        totalRecords.value = totalCount

        if (leaseRateMappings) {
          leaseRateMapping.value = { ...leaseRateMappings }
          originalMappings.value = { ...leaseRateMappings }
        }
      })

    // File Upload
    // Per-upload manual values are local only; reset them after each use so they are never persisted.
    const resetManualValues = () => {
      manualMileage.value = ''
      manualDuration.value = ''
    }

    const handleFileChange = (event: Event) => {
      const input = event.target as HTMLInputElement
      const file = input.files?.[0]

      // Validate companyId before proceeding
      if (!compId.value) {
        uploadPopupError.value = 'companyIdNotFound'
        input.value = ''
        return
      }

      const validationError = validateFile(file)
      if (validationError) {
        uploadPopupError.value = validationError
        input.value = ''
        return
      }

      uploadPopupError.value = ''
      selectedFile.value = file ?? null
    }

    const startUpload = async () => {
      const file = selectedFile.value
      if (!file) return

      clearMessages('upload')
      // Close the popup immediately; the page's existing loading state takes over.
      showUploadPopup.value = false

      try {
        isLoading.value = true

        const formData = new FormData()
        formData.append('file', file)

        // Only send manual mileage/duration when both are set; the backend then
        // ignores the mapped columns for this upload. Values are never persisted.
        if (manualMileage.value && manualDuration.value) {
          formData.append('mileage', manualMileage.value)
          formData.append('duration', manualDuration.value)
        }

        const response = await store.dispatch(leaseRateActions.UPLOAD_LEASERATES, {
          companyId: Number(compId.value),
          body: formData
        })
        const data = response?.data || response
        const leaseRateResponse = data?.leaseRateResponse

        // Extract error items from response (handles both array and object formats)
        let errorItems: LeaseRateErrorItem[] = []
        if (leaseRateResponse) {
          if (Array.isArray(leaseRateResponse)) {
            errorItems = leaseRateResponse.filter(item => item?.error)
          } else if (typeof leaseRateResponse === 'object') {
            // Handle object format with numeric keys: { "0": { error: "..." }, "1": { error: "..." }, ... }
            errorItems = Object.keys(leaseRateResponse)
              .filter(key => /^\d+$/.test(key))
              .map(key => leaseRateResponse[key])
              .filter(item => item?.error)
          }
        }

        // Show error popup based on status and error items
        if (data?.status === 'success' && errorItems.length > 0) {
          // Upload succeeded but some rows were skipped due to validation errors
          isPartialSuccess.value = true
          showErrorPopupWith(errorItems)
        } else if (data?.status === 'error') {
          // Full upload failure
          isPartialSuccess.value = false
          if (errorItems.length > 0) {
            showErrorPopupWith(errorItems)
          } else {
            // Handle string format: {"status":"error","leaseRateResponse":"Missing required columns: ..."}
            const errorMessage = typeof leaseRateResponse === 'string'
              ? leaseRateResponse
              : (data?.message || 'failedToProcessFile')
            showErrorPopupWith(errorMessage)
          }
        }

        // On success, refresh data
        if (data?.status === 'success') {
          await initialize()
          uploadSuccessKey.value = 'fileUploadedSuccessfully'
        } else {
          await initialize()
        }
      } catch (error) {
        showErrorPopupWith(extractErrorMessage(error as ApiErrorResponse))
      } finally {
        isLoading.value = false
        resetManualValues()
        selectedFile.value = null
        uploadPopupError.value = ''
        if (fileInput.value) fileInput.value.value = ''
      }
    }

    const openUploadPopup = () => {
      clearMessages('upload')
      resetManualValues()
      selectedFile.value = null
      uploadPopupError.value = ''
      showUploadPopup.value = true
    }

    const closeUploadPopup = () => {
      showUploadPopup.value = false
      resetManualValues()
      selectedFile.value = null
      uploadPopupError.value = ''
      if (fileInput.value) fileInput.value.value = ''
    }

    const triggerFileSelect = () => {
      // Manual mileage and duration must be set together or not at all.
      if (!manualMileage.value !== !manualDuration.value) {
        uploadPopupError.value = 'manualMileageDurationBothRequired'
        return
      }
      uploadPopupError.value = ''
      fileInput.value?.click()
    }

    const onUploadPopupButtonClick = () => {
      selectedFile.value ? startUpload() : triggerFileSelect()
    }

    const onMileageChange = (value: number) => {
      manualMileage.value = String(value)
      uploadPopupError.value = ''
    }

    const onDurationChange = (value: number) => {
      manualDuration.value = String(value)
      uploadPopupError.value = ''
    }

    // Mappings
    const saveMappings = () =>
      withLoading(async () => {
        clearMessages('mapping')

        let hasErrors = false
        MAPPING_FIELDS.forEach((field) => {
          const error = validateMappingField(leaseRateMapping.value[field])
          mappingErrors.value[field] = error
          if (error) hasErrors = true
        })

        if (hasErrors) {
          mappingErrorKey.value = t('_leaseRates.fixValidationErrors')
          return
        }

        const body = Object.fromEntries(
          MAPPING_FIELDS.map((f) => [f, leaseRateMapping.value[f].trim()])
        ) as companyLeaseRateMappingType

        const response = normalizeResponse(
          await store.dispatch(leaseRateActions.UPDATE_LEASERATE_MAPPINGS, {
            companyId: Number(compId.value),
            body
          })
        ) as { status?: string; message?: string }

        if (response?.status === 'error') {
          mappingErrorKey.value = response.message || 'failedToSaveMappings'
          return
        }

        originalMappings.value = { ...leaseRateMapping.value }
        mappingSuccessKey.value = response.message || 'mappingsSavedSuccessfully'
        await store.dispatch(leaseRateActions.GET_ALL_LEASERATES, Number(compId.value))
        refreshTableData()
      })

    const updateMappingField = (field: MappingField, value: string) => {
      clearMessages('mapping')
      leaseRateMapping.value[field] = value
      mappingErrors.value[field] = validateMappingField(value)
    }

    // Delete
    const handleDeleteLeaseRate = (item: LeaseRateItem) => {
      leaseRateToDelete.value = item
      showDeletePopup.value = true
    }

    const confirmDelete = () => {
      if (!leaseRateToDelete.value?.id) {
        console.error('Cannot delete: Invalid lease rate ID')
        showDeletePopup.value = false
        return
      }

      return withLoading(async () => {
        showDeletePopup.value = false

        const response = normalizeResponse(
          await store.dispatch(leaseRateActions.DELETE_LEASERATE, {
            companyId: Number(compId.value),
            leaseRateId: leaseRateToDelete.value?.id
          })
        ) as { status?: string; message?: string }

        if (response?.status === 'error') {
          showErrorPopupWith(response.message || 'failedToDeleteLeaseRate')
          return
        }

        await store.dispatch(leaseRateActions.GET_ALL_LEASERATES, Number(compId.value))
        await fetchCombinations()
        refreshTableData()
        leaseRateToDelete.value = null
      })
    }

    const cancelDelete = () => {
      showDeletePopup.value = false
      leaseRateToDelete.value = null
    }

    const closeErrorPopup = () => {
      showErrorPopup.value = false
      errorMessages.value = []
      isPartialSuccess.value = false
    }

    const closeSuccessPopup = () => {
      showSuccessPopup.value = false
    }

    onMounted(initialize)

    return {
      isLoading,
      uploadSuccessKey,
      mappingErrorKey,
      mappingSuccessKey,
      totalRecords,
      leaseRateMapping,
      mappingErrors,
      mappingsChanged,
      hasValidationErrors,
      showDeletePopup,
      showUploadPopup,
      fileInput,
      selectedFile,
      manualMileage,
      manualDuration,
      uploadPopupError,
      mileageOptions: MILEAGE_OPTIONS,
      durationOptions: DURATION_OPTIONS,
      showErrorPopup,
      showSuccessPopup,
      isPartialSuccess,
      carPolicyCombinations,
      errorMessages,
      leaseRateToDelete,
      tableHeadings,
      tableData,
      handleFileChange,
      startUpload,
      openUploadPopup,
      closeUploadPopup,
      triggerFileSelect,
      onUploadPopupButtonClick,
      onMileageChange,
      onDurationChange,
      saveMappings,
      updateMappingField,
      handleDeleteLeaseRate,
      confirmDelete,
      cancelDelete,
      closeErrorPopup,
      closeSuccessPopup,
      formatErrorForDisplay
    }
  }
})
</script>

<template>
  <div class="lease-rates-page">
    <!-- Page Header -->
    <div class="page-header">
      <h1>{{ $t('_leaseRates.title') }}</h1>
    </div>

    <!-- Main Content Container -->
    <div class="content-wrapper">
      <!-- Loading Overlay -->
      <div v-if="isLoading" class="overlay"></div>

      <!-- Content Card -->
      <div class="content-card">
        <!-- Upload Section -->
        <div class="upload-section">
          <!-- Lease Rate Data Card -->
          <div class="data-card">
            <div class="card-info">
              <h3>{{ $t('_leaseRates.leaseRateData') }}</h3>
              <div class="details">{{ $t('_leaseRates.additionalData') }}</div>

              <!-- Success/Error Messages -->
              <div v-if="uploadSuccessKey && uploadSuccessKey !== 'requiredListsCount'"
                class="message message--success">
                {{ $t(`_leaseRates.${uploadSuccessKey}`) }}
              </div>
              <div v-if="carPolicyCombinations.length > 0" class="message message--info">
                <span class="message__link" @click="showSuccessPopup = true">{{$t('_leaseRates.requiredListsCount', {
                  uploaded: carPolicyCombinations.filter(c => c.uploaded).length, total: carPolicyCombinations.length
                })}}</span>
                <button class="message__info-icon" @click="showSuccessPopup = true"
                  :title="$t('_leaseRates.requiredCombinationsMessage')"></button>
              </div>
              <!-- File Upload -->
              <div class="file-upload">
                <input ref="fileInput" type="file" name="file" accept=".xls,.xlsx" class="file-upload__hidden"
                  @change="handleFileChange" />
                <div class="file-upload__button">
                  <Button :text="$t('_leaseRates.uploadFile')" buttonType="add--left dark"
                    @onClick="openUploadPopup"></Button>
                </div>
              </div>
            </div>
          </div>

          <!-- Mapping Column Names Card -->
          <div class="data-card">
            <div class="card-block">
              <div class="card-info">
                <h3>{{ $t('_leaseRates.mappingColumnNames') }}</h3>
                <span class="details">{{ $t('_leaseRates.mappingDesc') }}</span>

                <!-- Success/Error Messages for Mappings -->
                <span v-if="mappingSuccessKey" class="message message--success">
                  {{ $t(`_leaseRates.${mappingSuccessKey}`) }}
                </span>
                <span v-if="mappingErrorKey" class="message message--error">
                  {{ mappingErrorKey }}
                </span>
              </div>
              <div class="card-info">
                <div class="form-section">
                  <div class="row">
                    <div class="half-width">
                      <div class="field-wrapper">
                        <InputElement type="text" name="leaseRate" :value="leaseRateMapping.leaseRate"
                          :placeholder="$t('_leaseRates.leaseRateDefault')"
                          @update:value="updateMappingField('leaseRate', $event)">
                          {{ $t('_leaseRates.leaseRate') }}
                        </InputElement>
                        <span v-if="mappingErrors.leaseRate" class="validation-error">
                          {{ mappingErrors.leaseRate }}
                        </span>
                      </div>
                    </div>
                    <div class="half-width">
                      <div class="field-wrapper">
                        <InputElement type="text" name="fuelAdvance" :value="leaseRateMapping.fuelAdvance"
                          :placeholder="$t('_leaseRates.fuelAdvanceDefault')"
                          @update:value="updateMappingField('fuelAdvance', $event)">
                          {{ $t('_leaseRates.fuelAdvance') }}
                        </InputElement>
                        <span v-if="mappingErrors.fuelAdvance" class="validation-error">
                          {{ mappingErrors.fuelAdvance }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div class="row">
                    <div class="half-width">
                      <div class="field-wrapper">
                        <InputElement type="text" name="vgsEpCode" :value="leaseRateMapping.vgsEpCode"
                          :placeholder="$t('_leaseRates.vgsEpCodeDefault')"
                          @update:value="updateMappingField('vgsEpCode', $event)">
                          {{ $t('_leaseRates.vgsEpCode') }}
                        </InputElement>
                        <span v-if="mappingErrors.vgsEpCode" class="validation-error">
                          {{ mappingErrors.vgsEpCode }}
                        </span>
                      </div>
                    </div>
                    <div class="half-width">
                      <div class="field-wrapper">
                        <InputElement type="text" name="mileage" :value="leaseRateMapping.mileage"
                          :placeholder="$t('_leaseRates.mileageDefault')"
                          @update:value="updateMappingField('mileage', $event)">
                          {{ $t('_leaseRates.mileage') }}
                        </InputElement>
                        <span v-if="mappingErrors.mileage" class="validation-error">
                          {{ mappingErrors.mileage }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div class="row">
                    <div class="half-width">
                      <div class="field-wrapper">
                        <InputElement type="text" name="duration" :value="leaseRateMapping.duration"
                          :placeholder="$t('_leaseRates.durationDefault')"
                          @update:value="updateMappingField('duration', $event)">
                          {{ $t('_leaseRates.duration') }}
                        </InputElement>
                        <span v-if="mappingErrors.duration" class="validation-error">
                          {{ mappingErrors.duration }}
                        </span>
                      </div>
                    </div>
                    <div class="half-width">
                      <div class="field-wrapper button-wrapper">
                        <Button :text="$t('_generalTerms.save')" buttonType="dark"
                          :disabled="!mappingsChanged || hasValidationErrors" @click="saveMappings"></Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Data Table Section -->
        <div v-if="!isLoading" class="table-section">
          <div class="table-wrapper">
            <div class="table-container">
              <div class="results-wrap">
                <span class="results-count">{{ totalRecords }}</span>
                <span>{{ $t('_leaseRates.leaseRateFilesUploaded') }}</span>
              </div>
              <ListingTable :headings="tableHeadings" :data="tableData" type="leaseRates"
                @deleteLeaseRate="handleDeleteLeaseRate" />
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-else class="loading-state">
          <Loader />
        </div>
      </div>
    </div>

    <!-- Upload File Popup (with optional manual mileage/duration) -->
    <SelectionPopup v-if="showUploadPopup" @onClick="closeUploadPopup">
      <template v-slot:selectionPopup>
        <div class="selectionPopup uploadPopup" role="dialog" aria-modal="true" aria-labelledby="upload-popup-title">
          <div class="popup-content">
            <div class="popup-header">
              <div id="upload-popup-title" class="title h4">{{ $t('_leaseRates.uploadFile') }}</div>
            </div>
            <div class="popup-body">
              <p class="upload-desc">{{ $t('_leaseRates.manualMileageDurationDesc') }}</p>
              <div class="upload-fields">
                <Dropdown :items="mileageOptions" :aria-label="$t('_leaseRates.mileage')"
                  :selectedOption="manualMileage || $t('_leaseRates.mileage')" @onChange="onMileageChange" />
                <Dropdown :items="durationOptions" :aria-label="$t('_leaseRates.duration')"
                  :selectedOption="manualDuration || $t('_leaseRates.duration')" @onChange="onDurationChange" />
              </div>
              <span v-if="uploadPopupError" class="message message--error">
                {{ $t(`_leaseRates.${uploadPopupError}`) }}
              </span>
              <div v-if="selectedFile" class="selected-file">{{ selectedFile.name }}</div>
            </div>
            <Button :text="selectedFile ? $t('_leaseRates.uploadFile') : $t('_leaseRates.chooseFile')"
              buttonType="dark upload-popup-button" @onClick="onUploadPopupButtonClick"></Button>
          </div>
        </div>
      </template>
    </SelectionPopup>

    <!-- Delete Confirmation Popup -->
    <Popup v-if="showDeletePopup" :centerPopup="true" @onProceed="confirmDelete" @onClick="cancelDelete">
      <div class="title h3">{{ $t('_warningPopup.confirmDelete') }}</div>
      <div class="text t2">{{ $t('_warningPopup.dataLost') }}</div>
      <p v-if="leaseRateToDelete" class="filename-highlight">{{ leaseRateToDelete.filename }}</p>
    </Popup>

    <!-- Error Popup -->
    <Popup v-if="showErrorPopup" :centerPopup="true" :setleaseRatePopup="true" :isRequiredPopup="true"
      :isStateConflicted="true" @revertSelectedOptions="closeErrorPopup" @onClick="closeErrorPopup">
      <div class="fileUploadErrors">
        <div class="title h3">{{ isPartialSuccess ? $t('_leaseRates.uploadSuccessWithSkippedRows') :
          $t('_leaseRates.uploadError') }}</div>
        <div class="error-messages-list">
          <div class="text t2 error-message-item" v-for="(error, index) in errorMessages" :key="index">
            {{ formatErrorForDisplay(error) }}
          </div>
        </div>
      </div>
    </Popup>

    <!-- Success Popup - Required Combinations -->
    <Popup v-if="showSuccessPopup" :centerPopup="true" :setleaseRatePopup="true" :isRequiredPopup="true"
      :isStateConflicted="true" @revertSelectedOptions="closeSuccessPopup" @onClick="closeSuccessPopup">
      <div class="fileUploadErrors">
        <div class="title h3">{{ $t('_leaseRates.requiredLeaseRateLists') }}</div>
        <div class="text t2">{{ $t('_leaseRates.requiredCombinationsMessage') }}</div>
        <div class="error-messages-list">
          <div class="text t2 message-item" v-for="(combo, index) in carPolicyCombinations" :key="index">
            {{ combo.duration }} {{ $t('_leaseRates.months') }} / {{ combo.mileage }} km -
            {{ combo.uploaded ? $t('_leaseRates.combinationUploaded') : $t('_leaseRates.combinationMissing') }}
          </div>
        </div>
      </div>
    </Popup>
  </div>
</template>

<style lang="sass" scoped>
@import 'sass/all.sass'
@import 'sass/mixins.sass'

// ============================================================================
// PAGE LAYOUT
// ============================================================================

.lease-rates-page
  height: 100%

  @media screen and (max-width: $mdBreakpoint)
    padding: 15px

.page-header
  display: flex
  justify-content: space-between

  @media screen and (max-width: $mdBreakpoint)
    align-items: center

  h1
    color: $colorHubble
    margin: 0

    @media screen and (max-width: $mdBreakpoint)
      font-size: 20px

// ============================================================================
// CONTENT WRAPPER & OVERLAY
// ============================================================================

.content-wrapper
  position: relative

.overlay
  background: rgba(255, 255, 255, 0.7)
  width: 100%
  height: 100%
  z-index: 1
  position: absolute
  top: 0
  left: 0

// ============================================================================
// CONTENT CARD
// ============================================================================

.content-card
  background: #fff
  box-shadow: 10px 10px 30px rgba(44, 0, 171, 0.07)
  border-radius: 10px
  padding: 3.3em 2em 6em
  margin: 1.5em 0

// ============================================================================
// UPLOAD SECTION
// ============================================================================

.upload-section
  display: flex
  gap: 2em
  margin-bottom: 3em
  position: relative

  @media screen and (max-width: $xsBreakpoint)
    flex-direction: column
    gap: 2em

.data-card
  display: flex
  flex-direction: column
  background-color: $colorHubbleBackgroundGrey
  padding: 3.2rem
  border-radius: 5px
  gap: 8px
  flex: 1

  &:first-child
    flex: 0.35

  .card-info
    display: flex
    flex-direction: column
    gap: 12px
    flex: 1

    h3
      font-size: 18px
      color: $colorHubbleDarkBlue
      margin: 0

    .details
      font-size: 14px
      color: $colorHubbleText
      line-height: 1.5

  .card-block
    display: flex
    align-self: stretch
    align-items: flex-start
    gap: 4rem
    .card-info
      &:first-child
        flex: 0.6

// ============================================================================
// MAPPINGS FORM
// ============================================================================
.form-section
  display: flex
  flex-direction: column
  gap: 0
  flex: 1

  .row
    display: flex
    gap: 3.2rem
    width: 100%
    margin-bottom: 0

    @media screen and (max-width: $mdBreakpoint - 1)
      flex-direction: column
      gap: 0

    &:last-child
      margin-bottom: 0

    .half-width
      flex: 1
      display: flex
      flex-direction: column

    button
      width: 100%
      padding: 13.5px 32px

      &:disabled
        opacity: 0.5
        cursor: not-allowed

  // Make input backgrounds match data-card background
  ::v-deep input[type="text"]
    background-color: $colorHubbleBackgroundGrey

  // Override input-element-wrap alignment to start instead of flex-end
  ::v-deep .input-element-wrap
    justify-content: flex-start
    height: auto

// ============================================================================
// FIELD WRAPPER & VALIDATION
// ============================================================================

.field-wrapper
  display: flex
  flex-direction: column
  min-height: 85px
  justify-content: flex-start
  margin-bottom: 0

  @media screen and (max-width: $mdBreakpoint - 1)
    min-height: auto
    margin-bottom: 1.5rem

  &.button-wrapper
    margin-top: 1rem

    @media screen and (max-width: $mdBreakpoint - 1)
      min-height: auto

.validation-error
  display: block
  color: $colorHubbleRed
  font-size: 12px
  margin-top: 8px
  line-height: 1.4
  padding-left: 2px
  min-height: 17px
// ============================================================================
// MESSAGES (Success/Error)
// ============================================================================

.message
  position: relative
  display: flex
  align-items: center
  padding: 12px 16px
  font-size: 14px
  line-height: 1.3
  font-weight: 600

  &:before
    content: ""
    width: 25px
    height: 25px
    margin-right: 10px

  &--success
    color: $colorSuccessDark
    background-color: $colorSuccessLight

  &--success:before
    @include backgroundImage('check.svg')

  &--error
    color: $colorHubbleRed
    background-color:#FEE2E2

  &--error:before
    @include backgroundImage('missing.svg')

  &--info
    color: #2C00AB
    background-color: transparent
    padding-left: 0
    gap: 5px
    font-size: 14px

  &--info:before
    display: none

  &__link
    cursor: pointer
    font-size: 14px

    &:hover
      opacity: 0.8

  &__info-icon
    width: 16px
    height: 16px
    min-width: 16px
    background-color: #2C00AB
    color: white
    border-radius: 50%
    border: none
    cursor: pointer
    padding: 0
    display: flex
    align-items: center
    justify-content: center
    flex-shrink: 0
    transition: opacity 0.2s ease
    

    &:before
      content: "?"
      font-family: 'Lato', sans-serif
      font-weight: bold
      font-size: 12px


    &:hover
      opacity: 0.8

// ============================================================================
// FILE UPLOAD
// ============================================================================

.file-upload
  position: relative

  &__hidden
    display: none

  &__button
    button
      padding: 1.3rem 2rem

      @media screen and (max-width: $xsBreakpoint)
        margin: auto

// ============================================================================
// UPLOAD FILE POPUP (selectionPopup slot)
// ============================================================================

.selectionPopup.uploadPopup
  .popup-content
    display: flex
    flex-direction: column
    padding: 3.2em

    .popup-header

      .title
        color: $colorHubble
        font-weight: 700
        padding: 0.5em 0
        border-bottom: 1px solid $colorHubbleGreyLines

    .popup-body
      display: flex
      flex-direction: column
      padding-top: 3.2em
      padding-bottom: 3.2em

      .upload-desc
        font-size: 14px
        line-height: 1.6
        color: $colorHubbleText
        margin: 0 0 2.8em 0

      .selected-file
        font-size: 14px
        color: $colorHubbleText
        font-weight: 600
        margin-top: 2em

      .upload-fields
        display: flex
        gap: 2rem

        @media screen and (max-width: $xsBreakpoint)
          flex-direction: column
          gap: 2.4rem

        // Two dropdowns side by side; reset the component's default side margins
        ::v-deep .dropdown
          flex: 1
          min-width: 0
          margin: 0

      .message
        margin-top: 2em

    .upload-popup-button
      width: 100%
      margin: auto
      padding: 0.8em 1.4em

// ============================================================================
// TABLE SECTION
// ============================================================================

.table-section
  @media screen and (max-width: $mdBreakpoint)
    background: #fff
    box-shadow: 10px 10px 30px rgba(44, 0, 171, 0.07)
    border-radius: 10px

.table-wrapper
  @media screen and (max-width: $xlBreakpoint)
    overflow-x: auto
    padding-bottom: 3em

    // Custom scrollbar
    &::-webkit-scrollbar
      width: 1px
      height: 5px

    &::-webkit-scrollbar-thumb
      background: $colorHubble
      border-radius: 5px

    &::-webkit-scrollbar-track
      background: $colorHubbleGreyLines
      border-radius: 5px

.table-container
  @media screen and (max-width: $xlBreakpoint)
    width: 92vw
    min-width: 1500px

  h2
    font-size: 16px
    font-family: 'Lato-semibold'
    border-bottom: 1px solid $colorHubbleGreyIcons
    margin: 1.4em 0 1.8em 0
    padding: 0 0 0.3em 0
    color: $colorHubble

  .results-wrap
    font-size: 14px
    margin: 1.5em 0 0
    display: flex
    align-items: center
    @media screen and (max-width: $mdBreakpoint)
      padding: 0 1em 0
    .results-count
      font-size: 14px
      background-color: $colorHubbleBackgroundGrey
      color: $colorHubbleText
      border-radius: 16px
      margin-right: 0.8em
      width: 34px
      display: flex
      text-align: center
      justify-content: center
      height: 22px
      align-items: center
    span
      &:last-child
        font-size: 14px
        white-space: nowrap
    &:after
      content: ''
      border-bottom: 1px solid $colorHubbleGreyLines
      width: 100%
      margin-left: 1em

// ============================================================================
// NO DATA MESSAGE
// ============================================================================

.no-data-message
  display: flex
  justify-content: center
  align-items: center
  padding: 3em 0

  p
    font-size: 16px
    color: $colorHubbleText
    margin: 0

// ============================================================================
// LOADING STATES
// ============================================================================

.loading-state
  display: flex
  justify-content: center
  align-items: center
  padding: 4em 0

.full-page-loader
  display: flex
  flex-direction: column
  justify-content: center
  align-items: center
  position: fixed
  z-index: 2
  top: 50%
  left: 50%
  transform: translate(-50%, -50%)
  width: 100%

  h3
    margin-top: 2em

// ============================================================================
// POPUP
// ============================================================================

.filename-highlight
  font-weight: bold
  color: $colorHubbleDarkBlue
  margin-top: 1rem
  text-align: center

.fileUploadErrors
  .h3.title
    color: $colorHubbleDarkBlue
    padding-bottom: 1rem
    border-bottom: 0.2rem solid $colorHubbleGreyIcons
    margin-bottom: 2rem

  .error-messages-list
    max-height: 400px
    overflow-y: auto
    margin-top: 1em

    &::-webkit-scrollbar
      width: 5px

    &::-webkit-scrollbar-thumb
      background: $colorHubble
      border-radius: 5px

    &::-webkit-scrollbar-track
      background: $colorHubbleGreyLines
      border-radius: 5px

  .error-message-item
    font-size: 14px
    margin: 0.8em 0
    padding-left: 1.5em
    position: relative
    text-align: left
    white-space: pre-line

    &:before
      content: "∙"
      position: absolute
      top: 11px
      transform: translateY(-50%)
      left: 0.5em
      font-weight: bold

// ============================================================================
// TABLE ALIGNMENT FIX FOR LEASE RATES
// ============================================================================

::v-deep .employees-listing.leaseRates
  .headings-wrap
    position: relative

    // Add spacer to match delete button space
    &::after
      content: ""
      flex-shrink: 0
      width: calc(1.8rem + 24px)
      background-color: $colorHubbleDarkBlue

  .details-wrap
    .detail
      .sub-detail-wrap
        gap: 0.5em
</style>
